import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  RowSelectionState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import { toast } from 'sonner';

import { EditFieldDialog } from './edit-field-dialog';
import { FilterMultiSelect } from './filter/filter-multi-select';
import { FilterPopoverContent } from './filter-popover-content';
import { Icon } from './icon';
import { Button } from './shadcn/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './shadcn/dropdown-menu';
import { Label } from './shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from './shadcn/tooltip';
import { ScrollableTable } from './table/scrollable-table';
import { useDisableUsers } from '@/api/mutations/useDisableUsers';
import { useEnableUsers } from '@/api/mutations/useEnableUsers';
import { useUpdateUser } from '@/api/mutations/useUpdateUser';
import { useUpdateUserCreditLimit } from '@/api/mutations/useUpdateUserCreditLimit';
import useFetchOrgUsers, { UserWithActivity } from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { useGetAllOrganisationUserRoles } from '@/api/queries/useGetOrganisationUserRoles';
import { useLazyUserMetadata } from '@/api/queries/useGetUserMetadata';
import { useUserContext } from '@/Contexts/User';
import { exportUsersToCSV } from '@/helpers/report-utils';
import { selectColumn } from '@/new/lib/table-utils';

const fallbackTableData: UserWithActivity[] = [];
const columnHelper = createColumnHelper<UserWithActivity>();

// Helper component to render last active date with lazy loading
function LastActiveCell({ userId, metadata }: { userId: string; metadata: ReturnType<typeof useLazyUserMetadata> }) {
  const { getMetadata, hasMetadata, isPending } = metadata;
  const userMetadata = getMetadata(userId);

  if (isPending(userId) || !hasMetadata(userId)) {
    return <div className="h-4 w-full animate-pulse rounded-md bg-gray-200/80" />;
  }

  if (!userMetadata) {
    return <span className="text-gray-500">Never</span>;
  }

  const date = new Date(userMetadata.lastActiveAt * 1000);
  const fullDateTime = date.toLocaleString();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{moment(date).fromNow()}</span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{fullDateTime}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Helper component to render email confirmed status with lazy loading
function EmailConfirmedCell({
  userId,
  metadata,
}: {
  userId: string;
  metadata: ReturnType<typeof useLazyUserMetadata>;
}) {
  const { getMetadata, hasMetadata, isPending } = metadata;
  const userMetadata = getMetadata(userId);

  if (isPending(userId) || !hasMetadata(userId)) {
    return <div className="h-4 w-16 animate-pulse rounded-md bg-gray-200/80" />;
  }

  if (!userMetadata) {
    return <span className="text-gray-500">-</span>;
  }

  return userMetadata.emailConfirmed ? (
    <span className="flex items-center gap-2">
      Yes <Icon className="lucide--check-circle-2 text-green-600" />
    </span>
  ) : (
    <span className="text-gray-500">No</span>
  );
}

// Shared filter function for Yes/No columns
function yesNoFilterFn(row: { getValue: (columnId: string) => string }, columnId: string, filterValue: string[]) {
  if (!filterValue || filterValue.length === 0) return true;
  return filterValue.includes(row.getValue(columnId));
}

// Shared cell renderer for Yes/No columns with check icon
function YesNoCell({ value }: { value: boolean }) {
  return value ? (
    <span className="flex items-center gap-2">
      Yes <Icon className="lucide--check-circle-2 text-green-600" />
    </span>
  ) : (
    <span className="text-gray-500">No</span>
  );
}

export function UsersTable({
  organisationId,
  onViewUserMatters,
  isSeatBased = false,
  showBlockControls,
}: {
  organisationId: string;
  onViewUserMatters?: (user: UserWithActivity) => void;
  showBlockControls?: boolean;
  isSeatBased?: boolean;
}) {
  const { user } = useUserContext();
  if (!user) return null;

  const { data: users } = useFetchOrgUsers(organisationId);
  const { data: roles } = useGetAllOrganisationUserRoles(organisationId);

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isExporting, setIsExporting] = useState(false);

  // Use lazy user metadata hook
  const userMetadata = useLazyUserMetadata();
  const [, setMetadataRefresh] = useState(0);

  const { mutate: updateUser } = useUpdateUser();
  const { mutate: updateCreditLimit } = useUpdateUserCreditLimit();
  const { mutate: disableUsers, isLoading: isDisabling } = useDisableUsers();
  const { mutate: enableUsers, isLoading: isEnabling } = useEnableUsers();

  const columns = useMemo(
    () => [
      ...(showBlockControls ? [selectColumn<UserWithActivity>()] : []),
      columnHelper.accessor('email', {
        size: 200,
        header: () => <span>Email Address</span>,
        cell: (info) => {
          const disabled = info.row.original.disabled;
          const emailValue = info.getValue();
          return (
            <div style={{ width: info.column.getSize() }} className={`${disabled ? 'text-gray-400' : ''}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1 truncate">
                    <span className="truncate">{emailValue}</span>
                    {disabled && <Icon className="lucide--ban shrink-0 text-xs" />}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{emailValue}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row.role?.name ?? '', {
        id: 'role',
        size: 80,
        header: () => <span>Role</span>,
        cell: (info) => {
          const role = info.row.original.role;
          return (!user.organisation?.isGlobalAdmin && role.name === 'Administrator') || info.row.original.disabled ? (
            <div className="px-0.5 py-0.5 pl-1.5 text-gray-500">{role.name}</div>
          ) : (
            <Select value={role.id} onValueChange={(roleId) => updateUser({ userId: info.row.original.id, roleId })}>
              <SelectTrigger className="-m-0.5 h-auto w-32 border-none p-1 pl-2 focus:outline-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(roles ?? [])
                  .filter((r) => r.name !== 'Administrator' || user?.organisation?.isGlobalAdmin === true)
                  .filter((r) => r.name !== 'Seat-based' || (users ?? []).some((u) => u.role?.name === 'Seat-based'))
                  .map((roleItem) => (
                    <SelectItem key={roleItem.id} value={roleItem.id}>
                      {roleItem.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          );
        },
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          return filterValue.includes(row.getValue(columnId));
        },
      }),
      ...(isSeatBased
        ? [
            columnHelper.accessor('fairUsageCreditLimit', {
              id: 'fairUsageCreditLimit',
              size: 100,
              header: () => <span>Fair Usage Limit</span>,
              cell: (info) => {
                const value = info.getValue();
                const userId = info.row.original.id;
                return (
                  <div className="flex items-center justify-between" style={{ width: info.column.getSize() }}>
                    <span>{value?.toLocaleString() ?? '—'}</span>
                    <EditFieldDialog
                      title="Edit Fair Usage Limit"
                      description="Update the monthly fair usage credit limit for this user."
                      fieldLabel="Credit Limit"
                      currentValue={String(value ?? '')}
                      onSave={(newValue) => {
                        const parsed = parseInt(newValue, 10);
                        if (isNaN(parsed) || parsed < 1) {
                          toast.error('Credit limit must be a positive number');
                          return;
                        }
                        updateCreditLimit({ orgId: organisationId, userId, creditLimit: parsed });
                      }}
                    />
                  </div>
                );
              },
            }),
          ]
        : []),
      columnHelper.accessor((row) => (row.hasBeenActive ? 'Yes' : 'No'), {
        id: 'hasBeenActive',
        header: () => <span>Onboarded</span>,
        cell: (info) =>
          info.getValue() === 'Yes' ? (
            <span className="flex items-center gap-2">
              Yes <Icon className="lucide--check-circle-2" />
            </span>
          ) : (
            <span className="text-gray-500">No</span>
          ),
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          return filterValue.includes(row.getValue(columnId));
        },
      }),
      columnHelper.accessor('matters', {
        size: 10,
        header: () => <span>No. of Matters</span>,
        cell: (info) => (
          <div style={{ width: info.column.getSize() }}>
            {info.getValue() ? info.getValue().length : <Icon className="lucide--eye-off size-3.5 text-gray-500" />}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'emailConfirmed',
        header: () => <span>Email Confirmed</span>,
        cell: (info) => <EmailConfirmedCell userId={info.row.original.id} metadata={userMetadata} />,
        enableSorting: false,
      }),
      columnHelper.accessor((row) => (row.kimActive ? 'Yes' : 'No'), {
        id: 'kimActive',
        header: () => <span>Kim Active</span>,
        cell: (info) => <YesNoCell value={info.row.original.kimActive} />,
        filterFn: yesNoFilterFn,
      }),
      columnHelper.accessor((row) => (row.runActive ? 'Yes' : 'No'), {
        id: 'runActive',
        header: () => <span>Run Active</span>,
        cell: (info) => <YesNoCell value={info.row.original.runActive} />,
        filterFn: yesNoFilterFn,
      }),
      ...(onViewUserMatters
        ? [
            columnHelper.display({
              id: 'manageMatters',
              cell: (info) => (
                <Button size="sm" onClick={() => onViewUserMatters(info.row.original)}>
                  Manage Matters
                </Button>
              ),
            }),
          ]
        : []),
      columnHelper.display({
        id: 'lastActiveAt',
        header: () => <span>Last Active</span>,
        cell: (info) => (
          <div className="w-32">
            <LastActiveCell userId={info.row.original.id} metadata={userMetadata} />
          </div>
        ),
        enableSorting: false,
      }),
    ],
    [userMetadata, roles, user, onViewUserMatters, isSeatBased, organisationId, updateCreditLimit, showBlockControls],
  );

  const table = useReactTable({
    data: users ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: !!showBlockControls,
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
      columnFilters,
      rowSelection,
    },
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
  });

  // Get column references for filters
  const roleColumn = table.getColumn('role');
  const onboardedColumn = table.getColumn('hasBeenActive');
  const kimActiveColumn = table.getColumn('kimActive');
  const runActiveColumn = table.getColumn('runActive');

  // Get filter values
  const roleFilterValue = (roleColumn?.getFilterValue() as string[]) ?? [];
  const onboardedFilterValue = (onboardedColumn?.getFilterValue() as string[]) ?? [];
  const kimActiveFilterValue = (kimActiveColumn?.getFilterValue() as string[]) ?? [];
  const runActiveFilterValue = (runActiveColumn?.getFilterValue() as string[]) ?? [];

  // Get unique role names for filter options, hiding Seat-based unless in use
  const hasSeatBasedUsers = useMemo(() => (users ?? []).some((u) => u.role?.name === 'Seat-based'), [users]);
  const roleOptions = useMemo(() => {
    return (roles ?? []).filter((r) => r.name !== 'Seat-based' || hasSeatBasedUsers).map((r) => r.name);
  }, [roles, hasSeatBasedUsers]);

  const onboardedOptions = ['Yes', 'No'];
  const kimActiveOptions = ['Yes', 'No'];
  const runActiveOptions = ['Yes', 'No'];

  const clearAllFilters = useCallback(() => {
    setColumnFilters([]);
  }, []);

  // Export functionality
  const handleExport = useCallback(async () => {
    if (!users) return;

    setIsExporting(true);
    try {
      await exportUsersToCSV(users, userMetadata);
    } finally {
      setIsExporting(false);
    }
  }, [users, userMetadata]);

  // Filter popover content
  const filterPopover = (
    <FilterPopoverContent clearAllFilters={clearAllFilters}>
      {roleColumn && (
        <div>
          <Label className="mb-2 block">Role</Label>
          <FilterMultiSelect
            values={roleFilterValue}
            options={roleOptions}
            column={roleColumn}
            placeholder="Select roles..."
          />
        </div>
      )}
      {onboardedColumn && (
        <div>
          <Label className="mb-2 block">Onboarded</Label>
          <FilterMultiSelect
            values={onboardedFilterValue}
            options={onboardedOptions}
            column={onboardedColumn}
            placeholder="Select onboarded status..."
          />
        </div>
      )}
      {kimActiveColumn && (
        <div>
          <Label className="mb-2 block">Kim Active</Label>
          <FilterMultiSelect
            values={kimActiveFilterValue}
            options={kimActiveOptions}
            column={kimActiveColumn}
            placeholder="Select Kim active status..."
          />
        </div>
      )}
      {runActiveColumn && (
        <div>
          <Label className="mb-2 block">Run Active</Label>
          <FilterMultiSelect
            values={runActiveFilterValue}
            options={runActiveOptions}
            column={runActiveColumn}
            placeholder="Select Run active status..."
          />
        </div>
      )}
    </FilterPopoverContent>
  );

  const selectedUserIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const selectedActiveUserIds = useMemo(
    () =>
      selectedUserIds.filter((id) => {
        const u = (users ?? []).find((u) => u.id === id);
        return u && !u.disabled;
      }),
    [selectedUserIds, users],
  );

  const selectedDisabledUserIds = useMemo(
    () =>
      selectedUserIds.filter((id) => {
        const u = (users ?? []).find((u) => u.id === id);
        return u?.disabled;
      }),
    [selectedUserIds, users],
  );

  const handleBlockUsers = useCallback(() => {
    if (selectedActiveUserIds.length === 0) return;
    disableUsers(selectedActiveUserIds, {
      onSuccess: () => setRowSelection({}),
    });
  }, [selectedActiveUserIds, disableUsers]);

  const handleUnblockUsers = useCallback(() => {
    if (selectedDisabledUserIds.length === 0) return;
    enableUsers(selectedDisabledUserIds, {
      onSuccess: () => setRowSelection({}),
    });
  }, [selectedDisabledUserIds, enableUsers]);

  // Export button + actions menu
  const actionButtons = (
    <>
      <Button onClick={handleExport} disabled={isExporting || !users?.length}>
        {isExporting ? <Icon className="lucide--loader-circle animate-spin" /> : <Icon className="lucide--download" />}
        <span>Download</span>
      </Button>
      {showBlockControls && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size={'iconSm'} disabled={selectedUserIds.length === 0} variant={'ghost'}>
              <Icon className="lucide--ellipsis-vertical" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={selectedActiveUserIds.length === 0 || isDisabling}
              onClick={handleBlockUsers}
              className="text-red-600 focus:text-red-700"
            >
              {isDisabling ? <Icon className="lucide--loader-circle animate-spin" /> : <Icon className="lucide--ban" />}
              <span>
                Block{' '}
                {selectedActiveUserIds.length > 0
                  ? `${selectedActiveUserIds.length} user${selectedActiveUserIds.length > 1 ? 's' : ''}`
                  : 'users'}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={selectedDisabledUserIds.length === 0 || isEnabling}
              onClick={handleUnblockUsers}
            >
              {isEnabling ? (
                <Icon className="lucide--loader-circle animate-spin" />
              ) : (
                <Icon className="lucide--check-circle-2" />
              )}
              <span>
                Unblock{' '}
                {selectedDisabledUserIds.length > 0
                  ? `${selectedDisabledUserIds.length} user${selectedDisabledUserIds.length > 1 ? 's' : ''}`
                  : 'users'}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );

  // Fetch metadata for currently visible users
  useEffect(() => {
    const visibleRows = table.getRowModel().rows;
    const visibleUserIds = visibleRows.map((row) => row.original.id);

    if (visibleUserIds.length > 0) {
      userMetadata.fetchMetadata(visibleUserIds).then(() => {
        // Force a re-render to show the fetched metadata
        setMetadataRefresh((prev) => prev + 1);
      });
    }
  }, [table.getState().pagination.pageIndex, users, globalFilter, columnFilters, userMetadata.cacheVersion]);

  return (
    <ScrollableTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      filters={{
        popover: filterPopover,
        count: columnFilters.length,
        clearAll: clearAllFilters,
      }}
      additionalButtons={actionButtons}
      plural="users"
    />
  );
}
